<script>
import Logo from './Logo.vue';
import PrimaryNavItem from './PrimaryNavItem.vue';
import OptionsMenu from './OptionsMenu.vue';
import AgentDetails from './AgentDetails.vue';
import NotificationBell from './NotificationBell.vue';
import CannyProductMenu from './CannyProductMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { ACCOUNT_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { BUS_EVENTS } from 'shared/constants/busEvents';

// Both Canny touchpoints live together in the rail footer: the changelog
// trigger (what shipped) and the feature board (what to ask for). Neither is a
// place you work in, so they stay out of the primary navigation.
const PRODUCT_FEEDBACK_KEY = 'product-feedback';

export default {
  components: {
    Logo,
    PrimaryNavItem,
    OptionsMenu,
    AgentDetails,
    NotificationBell,
    CannyProductMenu,
  },
  props: {
    logoSource: {
      type: String,
      default: '',
    },
    installationName: {
      type: String,
      default: '',
    },
    accountId: {
      type: Number,
      default: 0,
    },
    menuItems: {
      type: Array,
      default: () => [],
    },
    activeMenuItem: {
      type: String,
      default: '',
    },
    claudiaOnly: {
      type: Boolean,
      default: false,
    },
    showSupportLauncher: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      helpDocsURL: wootConstants.DOCS_URL,
      showOptionsMenu: false,
    };
  },
  computed: {
    scopedMenuItems() {
      if (!this.claudiaOnly) return this.menuItems;
      return this.menuItems.filter(
        item => item.keepInClaudiaOnly || item.claudiaOnlyLocked
      );
    },
    visibleMenuItems() {
      return this.scopedMenuItems.filter(
        item => item.key !== PRODUCT_FEEDBACK_KEY
      );
    },
    // Rendered in the footer next to the changelog trigger rather than in the
    // main list. Undefined when the account's menu doesn't carry the item.
    productFeedbackItem() {
      return this.scopedMenuItems.find(
        item => item.key === PRODUCT_FEEDBACK_KEY
      );
    },
    // Gate mirrors CannyChangelogWidget.canShowWidget: the changelog ships to
    // every account whenever there is an appID to load. Kill switch: deploy
    // with CANNY_APP_ID set to an empty string.
    canShowChangelog() {
      return Boolean(window.chatwootConfig?.cannyAppID);
    },
  },
  watch: {
    showSupportLauncher() {
      this.$nextTick(this.announceSupportSlot);
    },
  },
  mounted() {
    this.announceSupportSlot();
  },
  beforeDestroy() {
    this.announceSupportSlot();
  },
  methods: {
    frontendURL,
    announceSupportSlot() {
      this.$emitter.emit(BUS_EVENTS.CLOUDCHAT_SUPPORT_SLOT_CHANGED);
    },
    toggleOptions() {
      this.showOptionsMenu = !this.showOptionsMenu;
    },
    toggleAccountModal() {
      this.$emit('toggleAccounts');
    },
    toggleSupportChatWindow() {
      if (!window.$cloudchat?.toggle) {
        // SDK injected lazily on first activation — trigger it from here too.
        this.$emitter.emit(BUS_EVENTS.CLOUDCHAT_SUPPORT_ACTIVATE);
        return;
      }
      window.$cloudchat.toggle();
    },
    openNotificationPanel() {
      this.$track(ACCOUNT_EVENTS.OPENED_NOTIFICATIONS);
      this.$emit('openNotificationPanel');
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col justify-between w-16 h-full bg-white border-r dark:bg-slate-900 border-slate-50 dark:border-slate-800/50 rtl:border-l rtl:border-r-0"
  >
    <div
      class="flex flex-col items-center flex-1 min-h-0 overflow-y-auto [&>*]:shrink-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      <Logo
        :source="logoSource"
        :name="installationName"
        :account-id="accountId"
        class="m-4 mb-10"
      />
      <PrimaryNavItem
        v-for="menuItem in visibleMenuItems"
        :key="menuItem.toState"
        :icon="menuItem.icon"
        :name="menuItem.label"
        :to="menuItem.toState"
        :is-child-menu-active="menuItem.key === activeMenuItem"
        :open-in-new-page="menuItem.openInNewPage"
        :is-locked="claudiaOnly && !!menuItem.claudiaOnlyLocked"
      />
      <!-- Single entry point for both Canny touchpoints: the changelog (what
           shipped) and the feature board (what to ask for). It closes the main
           nav list rather than sitting in the user block below, which is for
           account/profile actions. The changelog popover is ~453px tall, and
           `initChangelog` measures this anchor to pick its direction, so from
           here it opens upward and stays on screen. Canny only computes that
           position on open, so the direction has to be right the first time —
           see changelogPosition in CannyChangelogWidget.vue, which also loads
           the SDK. -->
      <CannyProductMenu
        v-if="canShowChangelog"
        :product-feedback-item="productFeedbackItem"
      />
    </div>
    <div class="flex flex-col items-center justify-end pb-6">
      <!-- TODO_CLOUDCHAT: FAQ -->
      <!-- <PrimaryNavItem
        v-if="!isACustomBrandedInstance"
        icon="book-open-globe"
        name="DOCS"
        open-in-new-page
        :to="helpDocsURL"
      /> -->
      <NotificationBell @openNotificationPanel="openNotificationPanel" />
      <div
        v-if="showSupportLauncher"
        data-cloudchat-support-slot
        class="w-10 h-10 mb-6 shrink-0"
      />
      <AgentDetails @toggleMenu="toggleOptions" />
      <OptionsMenu
        :show="showOptionsMenu"
        @toggleAccounts="toggleAccountModal"
        @showSupportChatWindow="toggleSupportChatWindow"
        @openKeyShortcutModal="$emit('openKeyShortcutModal')"
        @close="toggleOptions"
      />
    </div>
  </div>
</template>
